import type { JsonValue } from './types';

/**
 * The value of a RICH_TEXT field (formatted text, D23): paragraphs and bulleted / numbered lists of inline text with
 * bold, italic, underline and links (http, https, mailto). Same rules as the backend RichText.
 *
 * { "blocks": [ { "type": "paragraph", "content": [ { "text": "Silné ", "bold": true }, { "text": "stránky" } ] },
 *               { "type": "bulletList", "items": [ [ { "text": "prvá" } ], [ { "text": "odkaz", "link": "https://…" } ] ] } ] }
 */
export interface RichInline {
  text: string;
  bold?: true;
  italic?: true;
  underline?: true;
  link?: string;
}

export type RichBlock = { type: 'paragraph'; content: RichInline[] } | { type: 'bulletList' | 'orderedList'; items: RichInline[][] };

export interface RichTextValue {
  blocks: RichBlock[];
}

export const RICH_TEXT_LIMITS = { blocks: 500, items: 500, inline: 500, link: 2000 };

/** http(s) and mailto links only (no javascript:, data: …); no spaces, quotes or angle brackets. */
const LINK = /^(https?:\/\/|mailto:)[^\s"<>]+$/i;
const MARKS = ['bold', 'italic', 'underline'] as const;
const INLINE_KEYS = new Set(['text', 'bold', 'italic', 'underline', 'link']);

function isObject(v: unknown): v is Record<string, JsonValue> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function hasOnly(o: Record<string, JsonValue>, keys: string[]): boolean {
  return Object.keys(o).every((k) => keys.includes(k));
}

export function isValidLink(link: unknown): link is string {
  return typeof link === 'string' && link.length <= RICH_TEXT_LIMITS.link && LINK.test(link);
}

function isInline(x: JsonValue): boolean {
  if (!isObject(x) || !Object.keys(x).every((k) => INLINE_KEYS.has(k))) return false;
  if (typeof x.text !== 'string' || x.text.length === 0) return false;
  if (MARKS.some((m) => x[m] !== undefined && x[m] !== true)) return false;
  return x.link === undefined || isValidLink(x.link);
}

function isInlineList(x: JsonValue): boolean {
  return Array.isArray(x) && x.length <= RICH_TEXT_LIMITS.inline && x.every(isInline);
}

/** A well-formed RICH_TEXT value (anything else is rejected as invalidType). */
export function isRichText(v: JsonValue | undefined): v is RichTextValue & { [key: string]: JsonValue } {
  if (!isObject(v) || !hasOnly(v, ['blocks']) || !Array.isArray(v.blocks) || v.blocks.length > RICH_TEXT_LIMITS.blocks) return false;
  return v.blocks.every((b) => {
    if (!isObject(b)) return false;
    if (b.type === 'paragraph') return hasOnly(b, ['type', 'content']) && isInlineList(b.content ?? null);
    if (b.type === 'bulletList' || b.type === 'orderedList') {
      const items = b.items;
      return hasOnly(b, ['type', 'items']) && Array.isArray(items) && items.length >= 1 && items.length <= RICH_TEXT_LIMITS.items && items.every(isInlineList);
    }
    return false;
  });
}

function inlineLists(v: RichTextValue): RichInline[][] {
  return v.blocks.flatMap((b) => (b.type === 'paragraph' ? [b.content] : b.items));
}

/** The number of characters of the text (what maxLength limits). */
export function richTextLength(v: RichTextValue): number {
  return inlineLists(v).reduce((n, list) => n + list.reduce((m, x) => m + x.text.length, 0), 0);
}

/** No text other than spaces: the field counts as empty. */
export function richTextIsBlank(v: RichTextValue): boolean {
  return inlineLists(v).every((list) => list.every((x) => x.text.trim() === ''));
}

/** Plain text: one line per paragraph and list item ("• …", "1. …"). */
export function richTextPlain(v: RichTextValue): string {
  const line = (list: RichInline[]) => list.map((x) => x.text).join('');
  return v.blocks
    .flatMap((b) => (b.type === 'paragraph' ? [line(b.content)] : b.items.map((item, i) => `${b.type === 'bulletList' ? '•' : `${i + 1}.`} ${line(item)}`)))
    .join('\n');
}
